import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../lib/api';
import { useTenantFormat } from '../../hooks/useTenantFormat';
import { useToast } from '../../hooks/useToast';
import { GlassCard } from '../../components/GlassCard';
import BrandLogo from '../../components/BrandLogo';
import { Printer, Download, Clock } from 'lucide-react';
import { arrayToCsvBlob, downloadBlob } from '../../lib/csv';

export default function Aging() {
  const { token, tenant } = useAuth();
  const toast = useToast();
  const { formatMoney } = useTenantFormat();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState('receivable');

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    api.erpAging(token, { type }).then(setData).catch(e => { if (e.status !== 401) toast.show(e.message, 'error'); }).finally(() => setLoading(false));
  }, [token, type, toast]);

  const exportCsv = () => {
    if (!data?.aging?.length) return;
    const headers = ['entity_name', 'current', 'days_1_30', 'days_31_60', 'days_61_90', 'days_91_plus', 'total'];
    const labels = ['Name', 'Current', '1–30 days', '31–60 days', '61–90 days', '91+ days', 'Total'];
    downloadBlob(arrayToCsvBlob(data.aging, headers, labels), `aging-${type}-${new Date().toISOString().slice(0,10)}.csv`);
    toast.show('CSV downloaded', 'success');
  };

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="report-aging-page">
      {/* Print-only header */}
      <div className="hidden print:block mb-6">
        <div className="flex items-center gap-3 mb-2">
          <BrandLogo size={32} theme="light" />
          <div>
            <h1 className="text-xl font-extrabold text-gray-900">{tenant?.name || '1000AD'}</h1>
            <p className="text-gray-500 text-xs">Aging Report ({type === 'receivable' ? 'Receivables' : 'Payables'}) · Generated {new Date().toLocaleDateString('en-IN')}</p>
          </div>
        </div>
      </div>

      {/* Screen header */}
      <div className="flex items-center justify-between mb-5 print:hidden">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center">
            <Clock size={20} className="text-amber-600" />
          </div>
          <div>
            <h1 className="text-gray-900 font-extrabold text-xl">Aging Report</h1>
            <p className="text-gray-500 text-xs">As of {data?.as_of ? new Date(data.as_of).toLocaleDateString('en-IN') : '—'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} disabled={!data?.aging?.length} data-testid="aging-export-csv" className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200 disabled:opacity-40"><Download size={14} /> CSV</button>
          <button onClick={() => window.print()} data-testid="aging-print" className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-purple text-white text-sm font-semibold hover:opacity-90"><Printer size={14} /> Print</button>
        </div>
      </div>

      {/* Type toggle */}
      <div className="flex gap-2 mb-5 print:hidden">
        {['receivable', 'payable'].map(t => (
          <button key={t} onClick={() => setType(t)} data-testid={`aging-type-${t}`}
            className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${type === t ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100'}`}>
            {t === 'receivable' ? 'Receivables' : 'Payables'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : data?.aging?.length > 0 ? (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            {[
              { label: 'Current', value: data.totals?.current },
              { label: '1–30 days', value: data.totals?.days_1_30 },
              { label: '31–60 days', value: data.totals?.days_31_60 },
              { label: '61–90+ days', value: (data.totals?.days_61_90 || 0) + (data.totals?.days_91_plus || 0) },
            ].map(k => (
              <GlassCard key={k.label} className="p-4">
                <p className="text-gray-500 text-[10px] font-bold uppercase tracking-wider">{k.label}</p>
                <p className="text-gray-900 font-extrabold text-lg tabular-nums mt-1">{formatMoney(k.value)}</p>
              </GlassCard>
            ))}
          </div>

          {/* Table */}
          <GlassCard className="overflow-hidden" data-testid="aging-table-card">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="aging-table">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left text-gray-500 text-xs font-bold uppercase tracking-wider px-5 py-3">Name</th>
                    <th className="text-right text-gray-500 text-xs font-bold uppercase tracking-wider px-5 py-3">Current</th>
                    <th className="text-right text-gray-500 text-xs font-bold uppercase tracking-wider px-5 py-3">1–30d</th>
                    <th className="text-right text-gray-500 text-xs font-bold uppercase tracking-wider px-5 py-3">31–60d</th>
                    <th className="text-right text-gray-500 text-xs font-bold uppercase tracking-wider px-5 py-3">61–90d</th>
                    <th className="text-right text-gray-500 text-xs font-bold uppercase tracking-wider px-5 py-3">91+d</th>
                    <th className="text-right text-gray-500 text-xs font-bold uppercase tracking-wider px-5 py-3">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.aging.map((r, i) => (
                    <tr key={i} className="border-b border-gray-100 hover:bg-gray-50/60">
                      <td className="px-5 py-3 text-gray-900 font-semibold">{r.entity_name}</td>
                      <td className="px-5 py-3 text-right text-gray-700 tabular-nums">{formatMoney(r.current)}</td>
                      <td className="px-5 py-3 text-right text-gray-700 tabular-nums">{formatMoney(r.days_1_30)}</td>
                      <td className="px-5 py-3 text-right text-gray-700 tabular-nums">{formatMoney(r.days_31_60)}</td>
                      <td className="px-5 py-3 text-right text-gray-700 tabular-nums">{formatMoney(r.days_61_90)}</td>
                      <td className="px-5 py-3 text-right text-amber-600 tabular-nums font-semibold">{formatMoney(r.days_91_plus)}</td>
                      <td className="px-5 py-3 text-right text-gray-900 font-extrabold tabular-nums">{formatMoney(r.total)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-300 bg-gray-50">
                    <td className="px-5 py-3 text-gray-900 font-extrabold">TOTAL</td>
                    <td className="px-5 py-3 text-right text-gray-900 font-extrabold tabular-nums">{formatMoney(data.totals?.current)}</td>
                    <td className="px-5 py-3 text-right text-gray-900 font-extrabold tabular-nums">{formatMoney(data.totals?.days_1_30)}</td>
                    <td className="px-5 py-3 text-right text-gray-900 font-extrabold tabular-nums">{formatMoney(data.totals?.days_31_60)}</td>
                    <td className="px-5 py-3 text-right text-gray-900 font-extrabold tabular-nums">{formatMoney(data.totals?.days_61_90)}</td>
                    <td className="px-5 py-3 text-right text-amber-600 font-extrabold tabular-nums">{formatMoney(data.totals?.days_91_plus)}</td>
                    <td className="px-5 py-3 text-right text-gray-900 font-extrabold tabular-nums">{formatMoney(data.totals?.total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </GlassCard>
        </>
      ) : (
        <GlassCard className="p-12 text-center"><p className="text-gray-500">No aging data available.</p></GlassCard>
      )}
    </div>
  );
}
